import { useEffect, useState } from "react";
import { Pagination } from "@mui/material";
import dayjs from "dayjs";
import styles from "./CreditReports.module.scss";
import { useAppSelector } from "@redux/hooks";
import { selectDashboardData } from "@redux/slices/CommonSlice";
import { useFetchFulfilmentHistoryQuery } from "@features/creditReports/creditReportsAPI";
import LayoutHeader from "@features/common/header/layoutHeader/LayoutHeader";
import ReportsTable from "@features/creditReports/reportsTable/ReportsTable";

/**
 * CreditReports Component
 *
 * Displays a paginated table of credit report fulfillment history with filtering capabilities.
 *
 * Features:
 * - Search functionality with debouncing
 * - Status filtering
 * - Date range filtering
 * - Pagination
 * - Displays current credit balance in header
 */
const CreditReports = () => {
  // ============================================================================
  // Redux State
  // ============================================================================
  const dashboardData = useAppSelector(selectDashboardData);

  // ============================================================================
  // Local State
  // ============================================================================
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();

  // ============================================================================
  // API Queries
  // ============================================================================
  const { data, isFetching } = useFetchFulfilmentHistoryQuery(
    {
      type: statusFilter || "all",
      page: currentPage,
      search_key: debouncedSearchTerm,
      ...(startDate && { start_date: dayjs(startDate).format("YYYY-MM-DD") }),
      ...(endDate && { end_date: dayjs(endDate).format("YYYY-MM-DD") }),
    },
    { refetchOnMountOrArgChange: true },
  );

  // ============================================================================
  // Effects
  // ============================================================================
  /**
   * Debounce search term to avoid excessive API calls
   * Waits 500ms after user stops typing before updating search
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ============================================================================
  // Event Handlers
  // ============================================================================
  /**
   * Handles search input changes
   * Resets pagination to first page when search changes
   */
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  /**
   * Handles status filter changes
   * Resets pagination to first page when filter changes
   */
  const handleStatusFilterChange = (value: string | null) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  /**
   * Handles date range filter changes
   * Resets pagination to first page when dates change
   */
  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={styles["page-container"]}>
      {/* Header with credit balance */}
      <LayoutHeader
        headerTitle="Credit report"
        amount={dashboardData?.amount || 0}
        currency={dashboardData?.currency || "AED"}
      />
      {/* Reports table with filters */}
      <ReportsTable
        data={data}
        isLoading={isFetching}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
      />

      {/* Pagination controls */}
      {data?.data?.length > 0 && (
        <div
          className={`${styles["pagination-container"]} report-pagination-container`}
        >
          <Pagination
            count={data?.total_page || 1}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            shape="rounded"
            className="compact-pagination"
          />
          <span>of {data?.total_page || 0} pages</span>
        </div>
      )}
    </div>
  );
};

export default CreditReports;
